import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { User } from '@aid/core/types/classes';
import { first, switchMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Permission } from '@aid/core/types/interfaces';
import { MembersService } from '@aid/members/services/members.service';
import { AuthService } from '@aid/auth/services';
import { OrganizationService } from '@aid/core/services/organization.service';
import { FirebaseService } from '@aid/core/firebase/firebase.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly url = 'user';
  private readonly http = inject(HttpClient);
  private readonly membersService = inject(MembersService);
  private readonly organizationService = inject(OrganizationService);
  private readonly authService = inject(AuthService);
  private readonly firebase = inject(FirebaseService);
  private _user$ = new BehaviorSubject<User | null>(null);
  private _userPermissions$ = new BehaviorSubject<{
    [code: string]: Permission;
  } | null>(null);
  private permissionsRequestId = 0;
  private permissionsMemberId: number | null = null;

  get() {
    if (this._user$.value) {
      return this._user$.asObservable();
    }

    return this.http.get<User>(this.url).pipe(
      tap(user => this._user$.next(new User(user))),
      switchMap(() => this._user$.asObservable())
    );
  }

  get user$(): Observable<User | null> {
    return this._user$.asObservable();
  }

  updateUser(user: User) {
    this._user$.next(user);
  }

  userSubject(): BehaviorSubject<User | null> {
    return this._user$;
  }

  get userValue(): User | null {
    return this._user$.value;
  }

  /** AuthGuard loads the signed-in user before authenticated routes render. */
  requireUser(): User {
    const user = this._user$.value;
    if (!user) {
      throw new Error('The signed-in user has not been loaded.');
    }
    return user;
  }

  getPermissions(): Observable<Permission[]> {
    const requestId = ++this.permissionsRequestId;
    const memberId = this.organizationService.organizationMember?.member;
    if (!memberId) {
      this.setPermissions([]);
      return of([]);
    }

    // Permissions belong to the active organization. Clear the previous
    // organization's values while the refresh is in flight so a role change
    // cannot briefly expose stale access.
    this.setPermissions([]);
    return this.membersService.permissions(memberId).pipe(
      tap((permissions: Permission[]) => {
        const currentMemberId = this.organizationService.organizationMember
          ?.member;
        if (
          requestId === this.permissionsRequestId &&
          currentMemberId === memberId
        ) {
          this.setPermissions(permissions);
        }
      })
    );
  }
  get isOwner() {
    return this.organizationService.organizationMember?.owner;
  }
  /**
   * Get the custom token from the API and authenticate on Firebase
   * Authentication on Firebase is Required because we are using Firestore as RealTime Database for real time events
   *
   * I saw that the app is working very slow if I don't call the user after signInWithCustomToken
   * IDK why this is happening
   */
  authenticateFirebase() {
    return this.http.get<string>('users/firebase-auth').pipe(
      switchMap(token => this.firebase.authenticate(token)),
      first()
    );
  }

  getCurrentMemberId() {
    const organizationId = this.organizationService.requireOrganization().id;
    for (const organization of this.requireUser().memberOf ?? []) {
      if (organization.id === organizationId) {
        return organization.member;
      }
    }
  }

  private setPermissions(permissions: Permission[]) {
    this.permissionsMemberId =
      this.organizationService.organizationMember?.member ?? null;
    const userPermissions: { [code: string]: Permission } = {};
    permissions.forEach(value => {
      userPermissions[value.code] = value;
    });
    this._userPermissions$.next(userPermissions);
  }

  get userPermissions() {
    return this._userPermissions$.value;
  }

  get userPermissions$(): Observable<{ [code: string]: Permission } | null> {
    return this._userPermissions$.asObservable();
  }

  hasPermission(checkPermissions: string[]): boolean {
    if (
      !this.userPermissions ||
      this.permissionsMemberId !==
        (this.organizationService.organizationMember?.member ?? null)
    ) {
      return false;
    }

    if (this.userPermissions.administrators) {
      return true;
    }

    for (const permission of checkPermissions) {
      if (
        permission === 'can_view_contact_persons' &&
        this.userPermissions.can_edit_contact_persons
      ) {
        continue;
      }
      if (!this.userPermissions[permission]) {
        return false;
      }
    }
    return true;
  }

  getSignatureUrl(id: number): Observable<string> {
    const url = `users/${id}/signature`;
    return this.http.get<string>(url);
  }

  logOut() {
    this.permissionsRequestId++;
    this.authService.logOut();
    this._user$.next(null);
    this.setPermissions([]);
  }
}
